/**
 * The staff door, from the browser's side: ending an Access session, and landing
 * somewhere a person can get back in.
 *
 * `donor.js` is the same job for the other door. The difference is that the donor session
 * is the Worker's own — a row it can delete and a redirect it controls — while this one
 * belongs to Cloudflare, and the only thing the app can do about it is ask the edge to
 * drop the cookie.
 *
 * Which it used to do as a whole-page navigation to `/cdn-cgi/access/logout`, and that is
 * the bug this file exists for. The logout endpoint is not a page anybody should be left
 * on. It answers a request that *carries* a `CF_Authorization` cookie by clearing it and
 * saying so, and a request that does not with **"No Access cookie found. Please login
 * first."** under a broken-document illustration and the title "Error ・ Cloudflare
 * Access" — no link, no way on, and nothing to say whether the session ended or something
 * went wrong. Signing out landed there often enough to be reported as a fault:
 *
 * - Any second request to it. The navigation clears the cookie, so a reload, a Back and
 *   forward again, or a tap on the standing-in screen's own "Continue to the sign-in
 *   page" link while the first navigation was still in flight all arrived without one.
 * - A session that had already gone. `/api/me` is asked once and cached, so a window left
 *   open past the Access session's six hours still draws a signed-in archive with a
 *   **Sign out** button in it, and pressing that button is a request with no cookie left
 *   to clear.
 *
 * So the logout is asked for as a request rather than travelled to, and the navigation —
 * the thing the person actually ends up looking at — goes to the app's own root instead.
 * Access answers `/` without a cookie with a 302 to the login page, which is where
 * somebody who has just signed out is trying to get to. Same two steps as the donor door,
 * in the same order, for the same reason.
 */
import { ACCESS_LOGOUT, SIGN_IN } from './leaving'

/*
 * `redirect: 'manual'` because the edge may answer this with a redirect to the team
 * domain's own logout, which is cross-origin and would make a followed fetch reject on
 * CORS — a rejection that says nothing about whether the logout worked. It does not
 * matter: cookies are applied from the response as it arrives, redirect or not, so the
 * `Set-Cookie` that clears `CF_Authorization` has already been honoured by the time this
 * resolves. Nothing reads the response.
 */
const LOGOUT_REQUEST = { credentials: 'same-origin', cache: 'no-store', redirect: 'manual' }

/**
 * Does the edge still let this browser in?
 *
 * Asked of `/` — the page about to be navigated to, so the answer is about the actual
 * destination and not a proxy for it. With `redirect: 'manual'` an admitted request is
 * the 200 that serves the app, and a refused one is an opaque redirect: `status: 0`,
 * `ok: false`, the 302 towards the login page that this file is trying to arrive at.
 *
 * Pure, and separate from the request, because what it decides is a navigation and a test
 * that could watch one happen would need a browser to leave — the same reason
 * `reauthenticationPlan` and `shouldRefuseAsIdle` take their facts as arguments.
 *
 * A response that never arrived (`null`, the fetch having thrown) reads as "not
 * admitted". Something is wrong with the network rather than with the session, and of the
 * two places to send somebody whose sign-out could not be confirmed, the login page is
 * the one they can get somewhere from.
 */
export const accessStillAdmits = (res) => Boolean(res?.ok)

/**
 * End the Access session, then go to the login page.
 *
 * The fallback is the old behaviour, and it is here for the one case that would otherwise
 * be worse than the error page: a logout request that did not take. Landing on `/` with a
 * live cookie would put the person straight back into the archive they had just asked to
 * leave — a sign-out button that visibly does nothing. If the door is still open, the
 * whole-page navigation to the logout URL is what it always was: an unhelpful page to be
 * left on, but a session genuinely ended.
 *
 * Both branches are `replace` rather than `assign`, so the archive being left is not one
 * Back press away from being the current page again.
 */
export async function accessSignOut() {
  await fetch(ACCESS_LOGOUT, LOGOUT_REQUEST).catch(() => null)
  const after = await fetch(SIGN_IN, LOGOUT_REQUEST).catch(() => null)
  window.location.replace(accessStillAdmits(after) ? ACCESS_LOGOUT : SIGN_IN)
}
